import * as React from 'react';
import styled from 'styled-components';
import { Button } from './button/Button';
import { ButtonRow } from './button/ButtonRow';
import { tablet } from './BreakPoints';
import { colors } from './colors/color';
import { Flex } from './compositeComponents/FlexHelpers';
import { H2 } from './typography/Heading';

const DEFAULT_LEFT_OFFSET_PX = 160;

type ModalMessageProps = 
{
    show: boolean,
    title?:string,
    buttonText?:string,
    content: React.ReactNode,
    onClose: () => void,
}
export function ModalMessage(props:ModalMessageProps):JSX.Element {

    return (
        <>
            {props.show &&
                <Modal
                    showModal={props.show}
                    title={props.title || ''}
                    onCancel={props.onClose}
                    content={
                        <Flex flexDirection='column' gap={16}>
                            {props.content}
                            <ButtonRow>
                                <Button variant='text' text={props.buttonText || 'Ok'} onClick={props.onClose} />
                            </ButtonRow>
                        </Flex>
                    }
                />
            }
        </>
    );
}


type ModalProps = {
    title: string,
    showModal: boolean,
    onCancel?: () => void,
    width?:string,
    content?:React.ReactNode,
    /** Applied only while the desktop navigation occupies the left side. */
    leftOffsetPx?: number,
    overflowVisible?:boolean // For when date selector is used
};

export function Modal(props:ModalProps):JSX.Element {

    const modalWindowRef = React.useRef(null);

    
    function bodyHasScrollbar() {
        return document.body.scrollHeight > window.innerHeight;
      }

    React.useEffect(() => {
        if (props.showModal && bodyHasScrollbar()) {
            document.body.classList.add('modal-open-scroll');
            document.body.classList.remove('modal-open-no-scroll');
        } else if(props.showModal && !bodyHasScrollbar){
            document.body.classList.add('modal-open-no-scroll');
            document.body.classList.remove('modal-open-scroll');
        } 
        
        return () => {
            document.body.classList.remove('modal-open-scroll');
            document.body.classList.remove('modal-open-no-scroll');
        };
        
      }, [props.showModal]);
    return (
        <>
        {props.showModal &&
            <PageWrapper>
                <StyledModal ref={modalWindowRef} $width={props.width} $leftOffsetPx={props.leftOffsetPx ?? DEFAULT_LEFT_OFFSET_PX}>
                    <ModalWindow>
                        <Flex flexDirection='column' gap={16}>

                            <Flex flexDirection='row' justifyContent='space-between'>
                                <H2 color="gray">{props.title}</H2>
                                {/* <div style={{cursor:'pointer'}} onClick={() => props.isOpen = false}>X</div> */}
                                <Button icon='actions_close' onClick={ props.onCancel} variant='text' size={"32px"} />
                            </Flex>
                            <ModalContent id="content" overflow={props.overflowVisible===true ? 'visible' : 'auto'}>
                                {props.content}
                            </ModalContent>
                        </Flex>
                    </ModalWindow>
                </StyledModal>
            </PageWrapper>
            }
        </>
    );
}


const StyledModal = styled.div<{ $width?:string, $leftOffsetPx:number }>`
    position: relative;
    width:${props => props.$width ? props.$width : '600px'};
    max-width: calc(100vw - ${props => (props.$leftOffsetPx * 2) + 32}px);
    transform: translateX(${props => props.$leftOffsetPx}px);
    min-height: 400px;
    max-height: calc(100vh - 32px);
    margin-top: 40px;

    @media (max-width: ${tablet}px) {
        max-width: 100%;
        transform: none;
    }
`;


const PageWrapper = styled.div`
    position:fixed;
    left: 0;
    top: 0;
    z-index: 1000;
    width: 100%;
    /* Cap the backdrop when mobile page overflow expands percentage widths. */
    max-width: 100vw;
    height: 100%;
    box-sizing: border-box;
    padding: 16px;
    background-color: rgba(0,0,0, 0.64);
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: flex-start;
`;

const ModalWindow = styled.div`
    background: ${colors.modal.background};
    width:100%;
    border-radius:4px;
    padding: 24px 16px 16px 16px;
`;

const ModalContent = styled.div<{overflow:'visible'|'auto'}>`
    max-height: calc(100vh - 150px);
    box-sizing: border-box;
    padding-bottom: 8px;
    padding-left: 8px;
    padding-right: 8px;
    overflow: ${props => props.overflow};
`;